import React, { useState, useEffect } from 'react';
import {
    Container, Paper, Typography, Box, TextField, Button,
    Grid, Alert, Card, CardContent, Divider, Switch,
    FormControlLabel, Dialog, DialogTitle, DialogContent,
    DialogActions, CircularProgress, Chip, IconButton
} from '@mui/material';
import {
    CloudUpload as CloudUploadIcon,
    Delete as DeleteIcon,
    Security as SecurityIcon,
    VpnKey as VpnKeyIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Science as ScienceIcon
} from '@mui/icons-material';
import api from '../services/api';

function AndamentosAPIConfig() {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [config, setConfig] = useState({
        tipo_autenticacao: 'NENHUMA',
        api_token: '',
        api_key: '',
        api_url: 'https://api.cnj.gov.br',
        api_tipo: 'CNJ',
        modo_teste: true
    });
    const [certificadoFile, setCertificadoFile] = useState(null);
    const [certificadoSenha, setCertificadoSenha] = useState('');
    const [hasCertificado, setHasCertificado] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [testDialogOpen, setTestDialogOpen] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [numeroProcessoTeste, setNumeroProcessoTeste] = useState('');

    useEffect(() => {
        loadConfig();
    }, []);

    const loadConfig = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await api.get('/andamentos-api-config');
            const data = response.data;
            // IMPORTANTE: Limpa URL se vier com tribunal específico (corrige URLs antigas)
            let apiUrl = data.api_url || 'https://api-publica.datajud.cnj.jus.br';
            if (apiUrl.includes('/api_publica_')) {
                // Remove tribunal específico se estiver na URL
                apiUrl = apiUrl.split('/api_publica_')[0];
                apiUrl = apiUrl.replace(/\/+$/, '');
                console.log('[CONFIG] URL corrigida ao carregar:', data.api_url, '→', apiUrl);
            }
            
            setConfig({
                tipo_autenticacao: data.tipo_autenticacao || 'NENHUMA',
                api_token: data.api_token || '',
                api_key: data.api_key || '',
                api_url: apiUrl, // Usa URL limpa (sem tribunal específico)
                api_tipo: data.api_tipo || 'CNJ',
                modo_teste: data.modo_teste !== undefined ? data.modo_teste : true
            });
            setHasCertificado(!!data.certificado_path);
        } catch (error) {
            console.error('Erro ao carregar configuração:', error);
            setError('Erro ao carregar configuração');
        } finally {
            setLoading(false);
        }
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            const ext = file.name.toLowerCase().split('.').pop();
            if (!['pfx', 'p12', 'pem'].includes(ext)) {
                setError('Tipo de arquivo inválido. Use .pfx, .p12 ou .pem');
                return;
            }
            if (file.size > 5 * 1024 * 1024) {
                setError('Arquivo muito grande. Máximo 5MB');
                return;
            }
            setCertificadoFile(file);
            setError('');
        }
    };

    const handleUploadCertificado = async () => {
        if (!certificadoFile) {
            setError('Selecione um arquivo de certificado');
            return;
        }

        setSaving(true);
        setError('');
        setSuccess('');

        try {
            const formData = new FormData();
            formData.append('certificado', certificadoFile);
            if (certificadoSenha) {
                formData.append('certificado_senha', certificadoSenha);
            }

            await api.post('/andamentos-api-config/upload-certificado', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            setSuccess('Certificado enviado e criptografado com sucesso!');
            setCertificadoFile(null);
            setCertificadoSenha('');
            document.getElementById('certificado-input').value = '';
            loadConfig();
        } catch (error) {
            console.error('Erro ao enviar certificado:', error);
            setError(error.response?.data?.error || 'Erro ao enviar certificado');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteCertificado = async () => {
        setSaving(true);
        setError('');
        setSuccess('');

        try {
            await api.delete('/andamentos-api-config/certificado');
            setSuccess('Certificado removido com sucesso!');
            setHasCertificado(false);
            setDeleteDialogOpen(false);
            loadConfig();
        } catch (error) {
            console.error('Erro ao remover certificado:', error);
            setError(error.response?.data?.error || 'Erro ao remover certificado');
        } finally {
            setSaving(false);
        }
    };

    const handleSaveConfig = async () => {
        setSaving(true);
        setError('');
        setSuccess('');

        try {
            await api.post('/andamentos-api-config', config);
            setSuccess('Configuração salva com sucesso!');
            loadConfig();
        } catch (error) {
            console.error('Erro ao salvar configuração:', error);
            setError(error.response?.data?.error || 'Erro ao salvar configuração');
        } finally {
            setSaving(false);
        }
    };

    const handleTestConnection = async () => {
        if (!numeroProcessoTeste.trim()) {
            setError('Informe um número de processo para testar');
            return;
        }

        setTesting(true);
        setError('');
        setTestResult(null);

        try {
            const response = await api.post('/andamentos-api-config/testar', {
                numeroProcesso: numeroProcessoTeste
            });
            setTestResult({
                sucesso: true,
                modo_teste: response.data.modo_teste,
                mensagem: response.data.mensagem
            });
            setTestDialogOpen(true);
        } catch (error) {
            console.error('Erro ao testar conexão:', error);
            setTestResult({
                sucesso: false,
                mensagem: error.response?.data?.error || 'Erro ao testar conexão'
            });
            setTestDialogOpen(true);
        } finally {
            setTesting(false);
        }
    };

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Paper sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" mb={3}>
                    <SecurityIcon sx={{ mr: 2, fontSize: 32 }} color="primary" />
                    <Typography variant="h4" component="h1">
                        Configuração de API de Andamentos
                    </Typography>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                        {error}
                    </Alert>
                )}

                {success && (
                    <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
                        {success}
                    </Alert>
                )}

                <Grid container spacing={3}>
                    {/* Upload de Certificado */}
                    <Grid item xs={12}>
                        <Card variant="outlined">
                            <CardContent>
                                <Box display="flex" alignItems="center" mb={2}>
                                    <CloudUploadIcon sx={{ mr: 1 }} color="primary" />
                                    <Typography variant="h6">
                                        Certificado Digital
                                    </Typography>
                                </Box>
                                <Divider sx={{ mb: 2 }} />

                                {/* Informações sobre Segurança e LGPD */}
                                <Alert severity="info" sx={{ mb: 2 }}>
                                    <Typography variant="body2" component="div">
                                        <Box component="span" display="block" mb={0.5}>
                                            🔒 <strong>Criptografia de Arquivo:</strong> O arquivo é criptografado antes de salvar no disco.
                                        </Box>
                                        <Box component="span" display="block">
                                            🔐 <strong>Criptografia de Senha:</strong> A senha do certificado (se houver) é criptografada no banco, respeitando a Lei Geral de Proteção de Dados (LGPD).
                                        </Box>
                                    </Typography>
                                </Alert>

                                {hasCertificado ? (
                                    <Box>
                                        <Alert severity="info" sx={{ mb: 2 }}>
                                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                                <Typography>
                                                    Certificado configurado e criptografado
                                                </Typography>
                                                <Button
                                                    variant="outlined"
                                                    color="error"
                                                    size="small"
                                                    startIcon={<DeleteIcon />}
                                                    onClick={() => setDeleteDialogOpen(true)}
                                                >
                                                    Remover Certificado
                                                </Button>
                                            </Box>
                                        </Alert>
                                    </Box>
                                ) : (
                                    <Box>
                                        <Typography variant="body2" color="text.secondary" mb={2}>
                                            Faça upload do seu certificado digital (.pfx, .p12 ou .pem)
                                        </Typography>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} md={6}>
                                                <Button
                                                    variant="outlined"
                                                    component="label"
                                                    fullWidth
                                                    startIcon={<CloudUploadIcon />}
                                                >
                                                    Selecionar Certificado
                                                    <input
                                                        id="certificado-input"
                                                        type="file"
                                                        hidden
                                                        accept=".pfx,.p12,.pem"
                                                        onChange={handleFileChange}
                                                    />
                                                </Button>
                                                {certificadoFile && (
                                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                                        Arquivo: {certificadoFile.name}
                                                    </Typography>
                                                )}
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    type="password"
                                                    label="Senha do Certificado (opcional)"
                                                    value={certificadoSenha}
                                                    onChange={(e) => setCertificadoSenha(e.target.value)}
                                                    placeholder="Deixe em branco se não tiver senha"
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    onClick={handleUploadCertificado}
                                                    disabled={!certificadoFile || saving}
                                                    startIcon={saving ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                                                >
                                                    {saving ? 'Enviando...' : 'Enviar e Criptografar Certificado'}
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Configuração de Token/API Key */}
                    <Grid item xs={12}>
                        <Card variant="outlined">
                            <CardContent>
                                <Box display="flex" alignItems="center" mb={2}>
                                    <VpnKeyIcon sx={{ mr: 1 }} color="primary" />
                                    <Typography variant="h6">
                                        Autenticação por Token/API Key
                                    </Typography>
                                </Box>
                                <Divider sx={{ mb: 2 }} />

                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            select
                                            label="Tipo de Autenticação"
                                            value={config.tipo_autenticacao}
                                            onChange={(e) => setConfig({ ...config, tipo_autenticacao: e.target.value })}
                                            SelectProps={{
                                                native: true
                                            }}
                                        >
                                            <option value="NENHUMA">Nenhuma</option>
                                            <option value="TOKEN">Token</option>
                                            <option value="API_KEY">API Key</option>
                                            <option value="BASICA">Basic Auth</option>
                                            <option value="CERTIFICADO">Certificado Digital</option>
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="URL da API"
                                            value={config.api_url}
                                            onChange={(e) => {
                                                let url = e.target.value.trim();
                                                // IMPORTANTE: Remove tribunal específico se o usuário colocar
                                                // A URL deve ser apenas a base, o backend adiciona o tribunal automaticamente
                                                // Exemplo errado: https://api-publica.datajud.cnj.jus.br/api_publica_tjgo/_search
                                                // Exemplo correto: https://api-publica.datajud.cnj.jus.br
                                                if (url.includes('/api_publica_')) {
                                                    // Remove tudo após /api_publica_* incluindo o tribunal
                                                    url = url.split('/api_publica_')[0];
                                                    // Garante que não termina com /
                                                    url = url.replace(/\/+$/, '');
                                                }
                                                // Remove _search se o usuário colocar
                                                url = url.replace(/\/_search.*$/, '');
                                                setConfig({ ...config, api_url: url });
                                            }}
                                            helperText="URL base: https://api-publica.datajud.cnj.jus.br (sem tribunal - o sistema constrói dinamicamente: /api_publica_[tribunal]/_search baseado no código TR do número CNJ)"
                                            error={config.api_url && config.api_url.includes('/api_publica_')}
                                        />
                                    </Grid>
                                    {config.tipo_autenticacao === 'TOKEN' && (
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                type="password"
                                                label="Token de API"
                                                value={config.api_token}
                                                onChange={(e) => setConfig({ ...config, api_token: e.target.value })}
                                                helperText="Token será criptografado antes de salvar"
                                            />
                                        </Grid>
                                    )}
                                    {config.tipo_autenticacao === 'API_KEY' && (
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                type="password"
                                                label="API Key (Chave Pública)"
                                                value={config.api_key}
                                                onChange={(e) => setConfig({ ...config, api_key: e.target.value })}
                                                helperText="Para API DataJud do CNJ, a Chave Pública deve ser solicitada via contato institucional com o CNJ ou seu tribunal. A chave será criptografada antes de salvar."
                                            />
                                        </Grid>
                                    )}
                                    {config.tipo_autenticacao === 'BASICA' && (
                                        <>
                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Usuário (API Key)"
                                                    value={config.api_key}
                                                    onChange={(e) => setConfig({ ...config, api_key: e.target.value })}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    type="password"
                                                    label="Senha (API Token)"
                                                    value={config.api_token}
                                                    onChange={(e) => setConfig({ ...config, api_token: e.target.value })}
                                                />
                                            </Grid>
                                        </>
                                    )}
                                    <Grid item xs={12}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={config.modo_teste}
                                                    onChange={(e) => setConfig({ ...config, modo_teste: e.target.checked })}
                                                />
                                            }
                                            label="Modo Teste (retorna dados de exemplo sem fazer requisição real)"
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={handleSaveConfig}
                                            disabled={saving}
                                            startIcon={saving ? <CircularProgress size={20} /> : <CheckCircleIcon />}
                                        >
                                            {saving ? 'Salvando...' : 'Salvar Configuração'}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Teste de Conexão */}
                    <Grid item xs={12}>
                        <Card variant="outlined">
                            <CardContent>
                                <Box display="flex" alignItems="center" mb={2}>
                                    <ScienceIcon sx={{ mr: 1 }} color="primary" />
                                    <Typography variant="h6">
                                        Testar Conexão
                                    </Typography>
                                </Box>
                                <Divider sx={{ mb: 2 }} />

                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={12} md={8}>
                                        <TextField
                                            fullWidth
                                            label="Número do Processo para Teste"
                                            value={numeroProcessoTeste}
                                            onChange={(e) => setNumeroProcessoTeste(e.target.value)}
                                            placeholder="Ex: 5000811-10.2025.8.13.0696"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            fullWidth
                                            onClick={handleTestConnection}
                                            disabled={testing || !numeroProcessoTeste.trim()}
                                            startIcon={testing ? <CircularProgress size={20} /> : <ScienceIcon />}
                                        >
                                            {testing ? 'Testando...' : 'Testar Conexão'}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Paper>

            {/* Dialog de Confirmação para Deletar Certificado */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Confirmar Remoção</DialogTitle>
                <DialogContent>
                    <Typography>
                        Tem certeza que deseja remover o certificado? Esta ação não pode ser desfeita.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
                    <Button onClick={handleDeleteCertificado} color="error" variant="contained" disabled={saving}>
                        {saving ? 'Removendo...' : 'Remover Certificado'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog de Resultado do Teste */}
            <Dialog open={testDialogOpen} onClose={() => setTestDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Resultado do Teste</DialogTitle>
                <DialogContent>
                    {testResult && (
                        <Box>
                            {testResult.sucesso ? (
                                <Alert severity="success" icon={<CheckCircleIcon />}>
                                    <Typography variant="body1" fontWeight="bold">
                                        {testResult.modo_teste ? 'Modo Teste' : 'Conexão Real'}
                                    </Typography>
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                        {testResult.mensagem}
                                    </Typography>
                                </Alert>
                            ) : (
                                <Alert severity="error" icon={<ErrorIcon />}>
                                    <Typography variant="body1" fontWeight="bold">
                                        Erro no Teste
                                    </Typography>
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                        {testResult.mensagem}
                                    </Typography>
                                </Alert>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTestDialogOpen(false)}>Fechar</Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}

export default AndamentosAPIConfig;

